<template>
  <div class="document-view-wrapper">
    <div class="document-library-modern">
      <!-- Header with search and controls -->
      <div class="library-header">
        <h3><i class="bi bi-folder2-open"></i> Documents</h3>
        <div class="header-controls">
          <div class="search-box" v-if="sortedDocuments.length > 0">
            <i class="bi bi-search"></i>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search documents..."
            />
            <button v-if="searchQuery" class="clear-search" @click="searchQuery = ''">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <select v-model="sortBy" class="sort-select" v-if="sortedDocuments.length > 0">
            <option value="date-desc">Newest First</option>
            <option value="date-asc">Oldest First</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
          </select>
          <div class="view-toggle" v-if="sortedDocuments.length > 0">
            <button
              :class="['view-btn', { active: viewMode === 'grid' }]"
              @click="viewMode = 'grid'"
              title="Grid view"
            ><i class="bi bi-grid-3x3-gap-fill"></i></button>
            <button
              :class="['view-btn', { active: viewMode === 'list' }]"
              @click="viewMode = 'list'"
              title="List view"
            ><i class="bi bi-list-ul"></i></button>
          </div>
        </div>
      </div>

      <!-- Documents Grid/List -->
      <div v-if="filteredDocuments.length > 0" class="documents-container">
        <div :class="viewMode === 'grid' ? 'documents-grid' : 'documents-list'">
          <div
            :class="viewMode === 'grid' ? 'document-card' : 'document-list-item'"
            v-for="document in filteredDocuments"
            :key="document.id"
          >
            <div class="doc-icon">
              <i class="bi bi-file-earmark-pdf-fill"></i>
            </div>
            <div class="doc-info">
              <h4 class="doc-name">{{ removeNumberFromStart(document.name) }}</h4>
              <div class="doc-meta">
                <span class="meta-item" v-if="document.created_date">
                  <i class="bi bi-calendar3"></i> {{ formatDate(document.created_date) }}
                </span>
              </div>
            </div>
            <div class="doc-actions">
              <button class="action-btn view-btn-action" @click="openDocument(document)" title="View">
                <i class="bi bi-eye"></i>
              </button>
              <a :href="getDocumentUrl(document.url)" :download="removeNumberFromStart(document.name)" class="action-btn download-btn" title="Download">
                <i class="bi bi-download"></i>
              </a>
              <button class="action-btn delete-btn" @click="deleteDocument(document)" title="Delete">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-modern">
        <div class="empty-icon">
          <i class="bi bi-folder2-open"></i>
        </div>
        <h4>No documents found</h4>
        <p v-if="searchQuery">Try a different search term</p>
        <p v-else>Documents will appear here when uploaded</p>
      </div>
    </div>

    <!-- Team Documents Section -->
    <div v-if="teamDocuments.length > 0" class="document-library-modern team-documents-section">
      <div class="library-header">
        <h3><i class="bi bi-people"></i> Team Documents</h3>
      </div>
      <div class="documents-container">
        <div :class="viewMode === 'grid' ? 'documents-grid' : 'documents-list'">
          <div
            :class="viewMode === 'grid' ? 'document-card' : 'document-list-item'"
            v-for="document in teamDocuments"
            :key="document.id"
          >
            <div class="doc-icon team-doc">
              <i class="bi bi-file-earmark-pdf-fill"></i>
            </div>
            <div class="doc-info">
              <h4 class="doc-name">{{ removeNumberFromStart(document.name) }}</h4>
              <div class="doc-meta">
                <span class="meta-item" v-if="document.created_date">
                  <i class="bi bi-calendar3"></i> {{ formatDate(document.created_date) }}
                </span>
              </div>
            </div>
            <div class="doc-actions">
              <button class="action-btn view-btn-action" @click="openDocument(document)" title="View">
                <i class="bi bi-eye"></i>
              </button>
              <a :href="getDocumentUrl(document.url)" :download="removeNumberFromStart(document.name)" class="action-btn download-btn" title="Download">
                <i class="bi bi-download"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Enhanced PDF Viewer Modal -->
    <div v-if="showPdfModal" class="document-viewer-modal-overlay" @click.self="closePdfModal">
      <div class="document-viewer-modal" :class="{ 'is-fullscreen': isFullscreen }">
        <!-- Modal Header -->
        <div class="viewer-header">
          <div class="viewer-title">
            <i class="bi bi-file-earmark-pdf-fill pdf-icon"></i>
            <h4>{{ removeNumberFromStart(selectedDocument.name) }}</h4>
          </div>
          <div class="viewer-controls">
            <button class="control-btn" @click="toggleFullscreen" :title="isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'">
              <i :class="isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-arrows-fullscreen'"></i>
            </button>
            <a v-if="selectedDocument.url" :href="getDocumentUrl(selectedDocument.url)" target="_blank" class="control-btn" title="Open in New Tab">
              <i class="bi bi-box-arrow-up-right"></i>
            </a>
            <a v-if="selectedDocument.url" :href="getDocumentUrl(selectedDocument.url)" :download="removeNumberFromStart(selectedDocument.name)" class="control-btn download" title="Download">
              <i class="bi bi-download"></i>
            </a>
            <button class="control-btn close" @click="closePdfModal" title="Close">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
        </div>

        <!-- Viewer Content -->
        <div class="viewer-content">
          <!-- PDF Viewer -->
          <div v-if="selectedDocument.url && isPdfFile(selectedDocument.url)" class="pdf-viewer-wrapper">
            <object
              :data="getDocumentUrl(selectedDocument.url)"
              type="application/pdf"
              class="pdf-embed">
              <iframe
                :src="getDocumentUrl(selectedDocument.url)"
                class="pdf-embed"
                frameborder="0">
              </iframe>
            </object>
          </div>

          <!-- Image Preview -->
          <div v-else-if="selectedDocument.url && isImageFile(selectedDocument.url)" class="image-viewer-wrapper">
            <img :src="getDocumentUrl(selectedDocument.url)" :alt="removeNumberFromStart(selectedDocument.name)" class="image-preview" />
          </div>

          <!-- Fallback for other file types -->
          <div v-else-if="selectedDocument.url" class="file-fallback">
            <div class="fallback-icon">
              <i class="bi bi-file-earmark"></i>
            </div>
            <h4>Preview not available</h4>
            <p>This file type cannot be previewed in the browser.</p>
            <a :href="getDocumentUrl(selectedDocument.url)" :download="removeNumberFromStart(selectedDocument.name)" class="download-btn">
              <i class="bi bi-download"></i> Download File
            </a>
          </div>
        </div>

        <!-- Mobile Bottom Bar -->
        <div class="viewer-bottom-bar">
          <a v-if="selectedDocument.url" :href="getDocumentUrl(selectedDocument.url)" target="_blank" class="action-btn">
            <i class="bi bi-box-arrow-up-right"></i>
            <span>Open</span>
          </a>
          <a v-if="selectedDocument.url" :href="getDocumentUrl(selectedDocument.url)" :download="removeNumberFromStart(selectedDocument.name)" class="action-btn primary">
            <i class="bi bi-download"></i>
            <span>Download</span>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Documents } from "@/services/documents-service.js";

export default {
  name: "DocumentView",
  props: {
    uploadedDocuments: {
      type: Array,
      required: true,
    },
    userData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      viewMode: "grid",
      searchQuery: "",
      sortBy: "date-desc",
      teamDocuments: [],
      showPdfModal: false,
      selectedDocument: {},
      isFullscreen: false,
    };
  },
  mounted() {
    if (Object.entries(this.userData).length !== 0) {
      this.getDocumentUploadsByTeamId();
    }
  },
  computed: {
    sortedDocuments() {
      return [...this.uploadedDocuments].sort((a, b) => {
        return new Date(b.created_date) - new Date(a.created_date);
      });
    },
    filteredDocuments() {
      let docs = [...this.uploadedDocuments];

      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        docs = docs.filter(doc =>
          this.removeNumberFromStart(doc.name).toLowerCase().includes(query)
        );
      }

      // Apply sorting
      docs.sort((a, b) => {
        switch (this.sortBy) {
          case 'date-desc':
            return new Date(b.created_date) - new Date(a.created_date);
          case 'date-asc':
            return new Date(a.created_date) - new Date(b.created_date);
          case 'name-asc':
            return this.removeNumberFromStart(a.name).localeCompare(this.removeNumberFromStart(b.name));
          case 'name-desc':
            return this.removeNumberFromStart(b.name).localeCompare(this.removeNumberFromStart(a.name));
          default:
            return 0;
        }
      });

      return docs;
    },
  },
  methods: {
    removeNumberFromStart(documentName) {
      if (!documentName) return '';
      return documentName.substring(documentName.indexOf("_") + 1);
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = now - date;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;

      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    },
    isPdfFile(url) {
      if (!url) return false;
      return url.toLowerCase().endsWith('.pdf');
    },
    getDocumentUrl(filename) {
      if (!filename) return '';
      // Serve documents through PHP endpoint
      if (window.location.href.indexOf("8080") > -1) {
        return `http://www.localhost:8888/serve-document.php?file=${encodeURIComponent(filename)}`;
      }
      return `/personal-training-BE/serve-document.php?file=${encodeURIComponent(filename)}`;
    },
    openDocument(document) {
      this.selectedDocument = { ...document };
      this.showPdfModal = true;
    },
    closePdfModal() {
      this.showPdfModal = false;
      this.selectedDocument = {};
      this.isFullscreen = false;
      document.body.style.overflow = '';
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      if (this.isFullscreen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    },
    isImageFile(url) {
      if (!url) return false;
      const ext = url.toLowerCase().split('.').pop();
      return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext);
    },
    async deleteDocument(document) {
      if (!confirm(`Delete "${this.removeNumberFromStart(document.name)}"?`)) return;
      try {
        const response = await Documents.find({
          action: "removeDocumentUpload",
          id: document.id,
        });
        if (response.status === 200) {
          this.$emit("documentDeleted");
        }
      } catch (error) {
        console.error("Error deleting document:", error);
      }
    },
    async getDocumentUploadsByTeamId() {
      const userData = {
        action: "getDocumentUploadsByTeamId",
        clubId: this.userData.clubId,
        teamId: this.userData.teamId,
      };
      const response = await Documents.find(userData).catch((error) => {
        console.log(error);
      });

      if (response && response.status === 200) {
        this.teamDocuments = response.data;
      }
    },
  },
};
</script>

<style scoped lang="scss">
// Ensure Bootstrap Icons display properly
.bi {
  font-family: "bootstrap-icons" !important;
  font-style: normal;
  font-weight: normal;
  display: inline-block;
  vertical-align: middle;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.document-view-wrapper {
  width: 100%;
}

.document-library-modern {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.team-documents-section {
  border-top: 3px solid #007bff;
}

.library-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 16px;

  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #1a1a2e;
    display: flex;
    align-items: center;
    gap: 10px;

    i {
      color: #28a745;
    }
  }

  .header-controls {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
  }
}

.search-box {
  display: flex;
  align-items: center;
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 0 12px;
  min-width: 200px;
  transition: all 0.2s;

  &:focus-within {
    border-color: #28a745;
    box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
  }

  i {
    color: #6c757d;
    font-size: 14px;
  }

  input {
    flex: 1;
    border: none;
    padding: 8px 10px;
    font-size: 14px;
    background: transparent;
    outline: none;

    &::placeholder {
      color: #adb5bd;
    }
  }

  .clear-search {
    background: none;
    border: none;
    padding: 4px;
    cursor: pointer;
    color: #6c757d;

    &:hover {
      color: #dc3545;
    }
  }
}

.sort-select {
  padding: 8px 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #28a745;
  }
}

.view-toggle {
  display: flex;
  gap: 4px;

  .view-btn {
    padding: 8px 12px;
    border: 2px solid #e9ecef;
    background: white;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      border-color: #28a745;
      color: #28a745;
    }

    &.active {
      background: #28a745;
      border-color: #28a745;
      color: white;
    }
  }
}

.documents-container {
  margin-top: 16px;
}

.documents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}

.documents-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.document-card {
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    border-color: #28a745;
  }

  .doc-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%);
    border-radius: 10px;
    margin-bottom: 12px;

    &.team-doc {
      background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
    }

    i {
      font-size: 24px;
      color: white;
    }
  }

  .doc-info {
    flex: 1;
    margin-bottom: 12px;

    .doc-name {
      font-size: 14px;
      font-weight: 600;
      color: #1a1a2e;
      margin: 0 0 8px 0;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .doc-meta {
      .meta-item {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        color: #6c757d;

        i {
          font-size: 12px;
        }
      }
    }
  }

  .doc-actions {
    display: flex;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid #e9ecef;
  }
}

.document-list-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  transition: all 0.2s;

  &:hover {
    border-color: #28a745;
    background: #f8f9fa;
  }

  .doc-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%);
    border-radius: 8px;
    flex-shrink: 0;

    &.team-doc {
      background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
    }

    i {
      font-size: 20px;
      color: white;
    }
  }

  .doc-info {
    flex: 1;

    .doc-name {
      font-size: 14px;
      font-weight: 600;
      color: #1a1a2e;
      margin: 0 0 4px 0;
    }

    .doc-meta {
      .meta-item {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        color: #6c757d;
      }
    }
  }

  .doc-actions {
    display: flex;
    gap: 8px;
  }
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;

  i {
    font-size: 16px;
  }

  &.view-btn-action {
    background: #e3f2fd;
    color: #1976d2;

    &:hover {
      background: #1976d2;
      color: white;
    }
  }

  &.download-btn {
    background: #e8f5e9;
    color: #388e3c;

    &:hover {
      background: #388e3c;
      color: white;
    }
  }

  &.delete-btn {
    background: #fee2e2;
    color: #dc3545;

    &:hover {
      background: #dc3545;
      color: white;
    }
  }
}

.empty-state-modern {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;

  .empty-icon {
    width: 80px;
    height: 80px;
    background: #f8f9fa;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;

    i {
      font-size: 32px;
      color: #adb5bd;
    }
  }

  h4 {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: #495057;
  }

  p {
    margin: 0;
    color: #6c757d;
    font-size: 14px;
  }
}

// Enhanced Document Viewer Modal
.document-viewer-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: fadeIn 0.2s ease;
}

.document-viewer-modal {
  background: white;
  border-radius: 12px;
  width: 100%;
  max-width: 1000px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: slideUp 0.3s ease;

  &.is-fullscreen {
    max-width: 100%;
    max-height: 100vh;
    border-radius: 0;

    .viewer-content {
      height: calc(100vh - 60px);
    }

    .pdf-embed {
      height: calc(100vh - 80px);
    }
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px 12px 0 0;

  .viewer-title {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;

    .pdf-icon {
      font-size: 1.5rem;
      color: #dc3545;
    }

    h4 {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 600;
      color: #1f2937;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .viewer-controls {
    display: flex;
    align-items: center;
    gap: 8px;

    .control-btn {
      width: 40px;
      height: 40px;
      border-radius: 8px;
      border: none;
      background: #f3f4f6;
      color: #4b5563;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      text-decoration: none;

      &:hover {
        background: #e5e7eb;
        color: #1f2937;
      }

      &.download {
        background: #dcfce7;
        color: #16a34a;

        &:hover {
          background: #bbf7d0;
          color: #15803d;
        }
      }

      &.close {
        background: #fee2e2;
        color: #dc2626;

        &:hover {
          background: #fecaca;
          color: #b91c1c;
        }
      }

      i {
        font-size: 1.1rem;
      }
    }
  }
}

.viewer-content {
  flex: 1;
  min-height: 0;
  padding: 20px;
  background: #f9fafb;
  overflow: hidden;
}

.pdf-viewer-wrapper {
  width: 100%;
  height: 100%;

  .pdf-embed {
    width: 100%;
    height: 65vh;
    min-height: 400px;
    border: none;
    border-radius: 8px;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
}

.image-viewer-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;

  .image-preview {
    max-width: 100%;
    max-height: 65vh;
    border-radius: 8px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
}

.file-fallback {
  text-align: center;
  padding: 60px 20px;

  .fallback-icon {
    width: 80px;
    height: 80px;
    background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;

    i {
      font-size: 2.5rem;
      color: white;
    }
  }

  h4 {
    margin: 0 0 8px;
    font-size: 1.25rem;
    color: #1f2937;
  }

  p {
    color: #6b7280;
    margin-bottom: 24px;
  }

  .download-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: linear-gradient(135deg, #28a745 0%, #218838 100%);
    color: white;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.2s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
    }
  }
}

.viewer-bottom-bar {
  display: none;
  padding: 12px 16px;
  background: white;
  border-top: 1px solid #e5e7eb;
  gap: 12px;
  border-radius: 0 0 12px 12px;

  .action-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    background: #f3f4f6;
    color: #4b5563;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s ease;

    &:hover {
      background: #e5e7eb;
    }

    &.primary {
      background: linear-gradient(135deg, #28a745 0%, #218838 100%);
      color: white;

      &:hover {
        transform: translateY(-1px);
      }
    }
  }
}

// Responsive
@media (max-width: 768px) {
  .library-header {
    flex-direction: column;
    align-items: flex-start;

    .header-controls {
      width: 100%;
    }
  }

  .search-box {
    flex: 1;
    min-width: auto;
  }

  .documents-grid {
    grid-template-columns: 1fr;
  }

  .document-viewer-modal-overlay {
    padding: 0;
  }

  .document-viewer-modal {
    max-height: 100vh;
    border-radius: 0;

    &.is-fullscreen {
      .viewer-content {
        height: calc(100vh - 120px);
      }
    }
  }

  .viewer-header {
    border-radius: 0;
    padding: 12px 16px;

    .viewer-title h4 {
      font-size: 0.95rem;
      max-width: 150px;
    }

    .viewer-controls .control-btn {
      width: 36px;
      height: 36px;

      &:not(.close) {
        display: none;
      }
    }
  }

  .viewer-content {
    padding: 12px;
  }

  .pdf-viewer-wrapper .pdf-embed {
    height: 55vh;
    min-height: 250px;
  }

  .viewer-bottom-bar {
    display: flex;
  }
}
</style>
